import { PREV_QUARTERS_MAPPER } from 'legacy/lut/components/widgets/public/partner360/configs/reports'

import { IMappedGeoMappingItem } from 'partner360/components/filters'
import { IQuarterlyMarketTableRow } from 'partner360/models/report/sobAnalyticsReports/iQuarterlyMarketTableRow'
import { ISobAnalyticsReportJoined } from 'partner360/models/report/sobAnalyticsReports/ISobAnalyticsReportJoined'

import { calculateDiffPP } from './calculateDiffPP'
import { calculateQoQ } from './calculateQoQ'
import { calculateSalesPerformance } from './calculateSalesPerformance'
import { calculateYoYGrowth } from './calculateYoYGrowth'
import { getExpectedExitFigures, getExpectedExitRowFields } from './getExpectedExitRowFields'
import { getSumOfNumbers } from './getSumOfNumbers'
import { calculateSobAnalyticsForPeriod } from './sobAnalyticReport/weightedAverage/calculateSobAnalyticsForPeriod'

interface IParams {
  geoMappings: IMappedGeoMappingItem[]
  data: ISobAnalyticsReportJoined[]
  coverageData: ISobAnalyticsReportJoined[]
  currentFYear: number
  quarters: number[]
  hasAppliedCuntriesFilter: boolean
  // adds IQuarterlyExpectedExitFields
  withExpectedExit?: boolean
}

export const mapSobAnalyticsReportJoinedToQuarterlyHierarchyNodes = ({
  geoMappings,
  data,
  coverageData,
  currentFYear,
  quarters,
  hasAppliedCuntriesFilter,
  withExpectedExit = false,
}: IParams): IQuarterlyMarketTableRow[] => {
  if (!data.length) {
    return []
  }

  const previousFYear = currentFYear - 1
  const quarterKey = quarters.join(',')
  const minQuarter = Math.min(...quarters)
  const prevQuarters = PREV_QUARTERS_MAPPER[quarterKey]
  const usePrevYearForPrevQuarter = minQuarter === 1

  return geoMappings
    .map<IQuarterlyMarketTableRow>(({ itemId, parentId, shortName, countries }) => {
      const dataByCountry = data.filter((it) => countries.includes(it.CountryCode))

      const prevYearData = dataByCountry.filter((it) => it.FYear === previousFYear)
      const currYearData = dataByCountry.filter((it) => it.FYear === currentFYear)
      const prevQuarterSource = usePrevYearForPrevQuarter ? prevYearData : currYearData

      const prevQuarterNdp = calculateSobAnalyticsForPeriod({
        data: prevQuarterSource,
        quarters: prevQuarters,
        netNdp: 'Ndp',
      })

      const prevQuarterNet = calculateSobAnalyticsForPeriod({
        data: prevQuarterSource,
        quarters: prevQuarters,
        netNdp: 'Net',
      })

      const prevYearNdp = calculateSobAnalyticsForPeriod({
        data: prevYearData,
        quarters,
        netNdp: 'Ndp',
      })
      const prevYearNet = calculateSobAnalyticsForPeriod({
        data: prevYearData,
        quarters,
        netNdp: 'Net',
      })

      const currQuarterNdp = calculateSobAnalyticsForPeriod({
        data: currYearData,
        quarters,
        netNdp: 'Ndp',
      })
      const currQuarterNet = calculateSobAnalyticsForPeriod({
        data: currYearData,
        quarters,
        netNdp: 'Net',
      })

      const currYearPlanNdp = calculateSobAnalyticsForPeriod({
        data: currYearData,
        quarters,
        netNdp: 'Ndp',
        isFuture: true,
      })
      const currYearPlanNet = calculateSobAnalyticsForPeriod({
        data: currYearData,
        quarters,
        netNdp: 'Net',
        isFuture: true,
      })

      const prevQuarterTNet = calculateSobAnalyticsForPeriod({
        data: prevQuarterSource,
        quarters: prevQuarters,
        netNdp: 'TNet',
      })
      const currQuarterTNet = calculateSobAnalyticsForPeriod({
        data: currYearData,
        quarters,
        netNdp: 'TNet',
      })
      const prevYearTNet = calculateSobAnalyticsForPeriod({
        data: prevYearData,
        quarters,
        netNdp: 'TNet',
      })
      const currYearPlanTNet = calculateSobAnalyticsForPeriod({
        data: currYearData,
        quarters,
        netNdp: 'TNet',
        isFuture: true,
      })

      const prevQuarterUTNet = calculateSobAnalyticsForPeriod({
        data: prevQuarterSource,
        quarters: prevQuarters,
        netNdp: 'UTNet',
      })
      const currQuarterUTNet = calculateSobAnalyticsForPeriod({
        data: currYearData,
        quarters,
        netNdp: 'UTNet',
      })
      const prevYearUTNet = calculateSobAnalyticsForPeriod({
        data: prevYearData,
        quarters,
        netNdp: 'UTNet',
      })
      const currYearPlanUTNet = calculateSobAnalyticsForPeriod({
        data: currYearData,
        quarters,
        netNdp: 'UTNet',
        isFuture: true,
      })

      const filteredCoverage = coverageData
        .filter((it) => countries.includes(it.CountryCode))
        .filter((it) => it.FYear === previousFYear)

      const expectedExitFields = withExpectedExit
        ? getExpectedExitRowFields({
            Ndp: getExpectedExitFigures({
              currYearData,
              quarters,
              netNdp: 'Ndp',
              prevYear: prevYearNdp,
              prevQuarter: prevQuarterNdp,
              currYearPlan: currYearPlanNdp,
            }),
            Net: getExpectedExitFigures({
              currYearData,
              quarters,
              netNdp: 'Net',
              prevYear: prevYearNet,
              prevQuarter: prevQuarterNet,
              currYearPlan: currYearPlanNet,
            }),
            TNet: getExpectedExitFigures({
              currYearData,
              quarters,
              netNdp: 'TNet',
              prevYear: prevYearTNet,
              prevQuarter: prevQuarterTNet,
              currYearPlan: currYearPlanTNet,
            }),
            UTNet: getExpectedExitFigures({
              currYearData,
              quarters,
              netNdp: 'UTNet',
              prevYear: prevYearUTNet,
              prevQuarter: prevQuarterUTNet,
              currYearPlan: currYearPlanUTNet,
            }),
          })
        : {}

      return {
        itemId,
        parentId: parentId || null,
        title: shortName,
        partnersCount: currYearData.length,
        hasChildren: countries.length > 1,
        order: currYearData[0]?.GeoMarketOrder ?? -1,

        /* PREVIOUS YEAR */
        prevYearActualsRevenue: prevYearNdp.actualsRevenue,
        prevYearActualsRevenueNet: prevYearNet.actualsRevenue,
        prevYearActualsRevenueTNet: prevYearTNet.actualsRevenue,
        prevYearActualsRevenueUTNet: prevYearUTNet.actualsRevenue,

        prevYearEstimatedActualsRevenue: prevYearNdp.estimatedActualsRevenue,
        prevYearEstimatedActualsRevenueNet: prevYearNet.estimatedActualsRevenue,
        prevYearEstimatedActualsRevenueTNet: prevYearTNet.estimatedActualsRevenue,
        prevYearEstimatedActualsRevenueUTNet: prevYearUTNet.estimatedActualsRevenue,

        prevYearSobActual: prevYearNdp.sob ?? prevYearNdp.plainSob,
        prevYearSobActualNet: prevYearNet.sob ?? prevYearNet.plainSob,
        prevYearSobActualTNet: prevYearTNet.sob ?? prevYearTNet.plainSob,
        prevYearSobActualUTNet: prevYearUTNet.sob ?? prevYearUTNet.plainSob,

        prevYearYoYPP: calculateDiffPP({
          sobActual: currQuarterNdp.sob ?? currQuarterNdp.plainSob,
          sobPlan: prevYearNdp.sob ?? prevYearNdp.plainSob,
        }),

        prevYearYoYPPNet: calculateDiffPP({
          sobActual: currQuarterNet.sob ?? currQuarterNet.plainSob,
          sobPlan: prevYearNet.sob ?? prevYearNet.plainSob,
        }),

        prevYearYoYPPTNet: calculateDiffPP({
          sobActual: currQuarterTNet.sob ?? currQuarterTNet.plainSob,
          sobPlan: prevYearTNet.sob ?? prevYearTNet.plainSob,
        }),

        prevYearYoYPPUTNet: calculateDiffPP({
          sobActual: currQuarterUTNet.sob ?? currQuarterUTNet.plainSob,
          sobPlan: prevYearUTNet.sob ?? prevYearUTNet.plainSob,
        }),

        prevYearVsPlan: calculateDiffPP({
          sobActual: currQuarterNdp.sob ?? currQuarterNdp.plainSob,
          sobPlan: currYearPlanNdp.sob ?? currYearPlanNdp.plainSob,
        }),

        prevYearVsPlanNet: calculateDiffPP({
          sobActual: currQuarterNet.sob ?? currQuarterNet.plainSob,
          sobPlan: currYearPlanNet.sob ?? currYearPlanNet.plainSob,
        }),

        prevYearVsPlanTNet: calculateDiffPP({
          sobActual: currQuarterTNet.sob ?? currQuarterTNet.plainSob,
          sobPlan: currYearPlanTNet.sob ?? currYearPlanTNet.plainSob,
        }),

        prevYearVsPlanUTNet: calculateDiffPP({
          sobActual: currQuarterUTNet.sob ?? currQuarterUTNet.plainSob,
          sobPlan: currYearPlanUTNet.sob ?? currYearPlanUTNet.plainSob,
        }),

        prevYearQuarterTamActuals: prevYearNdp.tam,
        prevYearQuarterTamActualsNet: prevYearNet.tam,
        prevYearQuarterTamActualsTNet: prevYearTNet.tam,
        prevYearQuarterTamActualsUTNet: prevYearUTNet.tam,

        /* PREVIOUS QUARTER */
        prevQuarterActualsRevenue: prevQuarterNdp.actualsRevenue,
        prevQuarterActualsRevenueNet: prevQuarterNet.actualsRevenue,
        prevQuarterActualsRevenueTNet: prevQuarterTNet.actualsRevenue,
        prevQuarterActualsRevenueUTNet: prevQuarterUTNet.actualsRevenue,

        prevQuarterEstimatedActualsRevenue: prevQuarterNdp.estimatedActualsRevenue,
        prevQuarterEstimatedActualsRevenueNet: prevQuarterNet.estimatedActualsRevenue,
        prevQuarterEstimatedActualsRevenueTNet: prevQuarterTNet.estimatedActualsRevenue,
        prevQuarterEstimatedActualsRevenueUTNet: prevQuarterUTNet.estimatedActualsRevenue,

        prevQuarterSobActuals: prevQuarterNdp.sob ?? prevQuarterNdp.plainSob,
        prevQuarterSobActualsNet: prevQuarterNet.sob ?? prevQuarterNet.plainSob,
        prevQuarterSobActualsTNet: prevQuarterTNet.sob ?? prevQuarterTNet.plainSob,
        prevQuarterSobActualsUTNet: prevQuarterUTNet.sob ?? prevQuarterUTNet.plainSob,

        /* CURRENT QUARTER */
        currQuarterActualsRevenue: currQuarterNdp.actualsRevenue,
        currQuarterActualsRevenueNet: currQuarterNet.actualsRevenue,
        currQuarterActualsRevenueTNet: currQuarterTNet.actualsRevenue,
        currQuarterActualsRevenueUTNet: currQuarterUTNet.actualsRevenue,

        currQuarterEstimatedActualsRevenue: currQuarterNdp.estimatedActualsRevenue,
        currQuarterEstimatedActualsRevenueNet: currQuarterNet.estimatedActualsRevenue,
        currQuarterEstimatedActualsRevenueTNet: currQuarterTNet.estimatedActualsRevenue,
        currQuarterEstimatedActualsRevenueUTNet: currQuarterUTNet.estimatedActualsRevenue,

        currQuarterSobActuals: currQuarterNdp.sob ?? currQuarterNdp.plainSob,
        currQuarterSobActualsNet: currQuarterNet.sob ?? currQuarterNet.plainSob,
        currQuarterSobActualsTNet: currQuarterTNet.sob ?? currQuarterTNet.plainSob,
        currQuarterSobActualsUTNet: currQuarterUTNet.sob ?? currQuarterUTNet.plainSob,

        /* YoY / QoQ */
        yoyRevenue:
          currYearPlanNdp.actualsRevenue && prevYearNdp.actualsRevenue
            ? ((currYearPlanNdp.actualsRevenue - prevYearNdp.actualsRevenue) /
                prevYearNdp.actualsRevenue) *
              100
            : null,

        yoyRevenueNet:
          currYearPlanNet.actualsRevenue && prevYearNet.actualsRevenue
            ? ((currYearPlanNet.actualsRevenue - prevYearNet.actualsRevenue) /
                prevYearNet.actualsRevenue) *
              100
            : null,

        yoyRevenueTNet:
          currYearPlanTNet.actualsRevenue && prevYearTNet.actualsRevenue
            ? ((currYearPlanTNet.actualsRevenue - prevYearTNet.actualsRevenue) /
                prevYearTNet.actualsRevenue) *
              100
            : null,

        yoyRevenueUTNet:
          currYearPlanUTNet.actualsRevenue && prevYearUTNet.actualsRevenue
            ? ((currYearPlanUTNet.actualsRevenue - prevYearUTNet.actualsRevenue) /
                prevYearUTNet.actualsRevenue) *
              100
            : null,

        yoyBaseline: calculateDiffPP({
          sobActual: currYearPlanNdp.sob || currYearPlanNdp.plainSob,
          sobPlan: prevYearNdp.sob || prevYearNdp.plainSob,
        }),

        yoyBaselineNet: calculateDiffPP({
          sobActual: currYearPlanNet.sob || currYearPlanNet.plainSob,
          sobPlan: prevYearNet.sob || prevYearNet.plainSob,
        }),

        yoyBaselineTNet: calculateDiffPP({
          sobActual: currYearPlanTNet.sob || currYearPlanTNet.plainSob,
          sobPlan: prevYearTNet.sob || prevYearTNet.plainSob,
        }),

        yoyBaselineUTNet: calculateDiffPP({
          sobActual: currYearPlanUTNet.sob || currYearPlanUTNet.plainSob,
          sobPlan: prevYearUTNet.sob || prevYearUTNet.plainSob,
        }),

        currYearQoQ: calculateQoQ({
          data: dataByCountry,
          netNdp: 'Ndp',
          fYear: currentFYear,
          quarters,
        }),

        currYearQoQNet: calculateQoQ({
          data: dataByCountry,
          netNdp: 'Net',
          fYear: currentFYear,
          quarters,
        }),

        currYearQoQTNet: calculateQoQ({
          data: dataByCountry,
          netNdp: 'TNet',
          fYear: currentFYear,
          quarters,
        }),

        currYearQoQUTNet: calculateQoQ({
          data: dataByCountry,
          netNdp: 'UTNet',
          fYear: currentFYear,
          quarters,
        }),

        partnerTamYoY:
          prevYearNdp.tam && currYearPlanNdp.tam
            ? ((currYearPlanNdp.tam - prevYearNdp.tam) / prevYearNdp.tam) * 100
            : null,

        partnerTamYoYNet:
          prevYearNet.tam && currYearPlanNet.tam
            ? ((currYearPlanNet.tam - prevYearNet.tam) / prevYearNet.tam) * 100
            : null,

        partnerTamYoYTNet:
          prevYearTNet.tam && currYearPlanTNet.tam
            ? ((currYearPlanTNet.tam - prevYearTNet.tam) / prevYearTNet.tam) * 100
            : null,

        partnerTamYoYUTNet:
          prevYearUTNet.tam && currYearPlanUTNet.tam
            ? ((currYearPlanUTNet.tam - prevYearUTNet.tam) / prevYearUTNet.tam) * 100
            : null,

        partnerTamActualsYoY:
          prevYearNdp.tam && currQuarterNdp.tam
            ? ((currQuarterNdp.tam - prevYearNdp.tam) / prevYearNdp.tam) * 100
            : null,

        partnerTamActualsYoYNet:
          prevYearNet.tam && currQuarterNet.tam
            ? ((currQuarterNet.tam - prevYearNet.tam) / prevYearNet.tam) * 100
            : null,

        partnerTamActualsYoYTNet:
          prevYearTNet.tam && currQuarterTNet.tam
            ? ((currQuarterTNet.tam - prevYearTNet.tam) / prevYearTNet.tam) * 100
            : null,

        partnerTamActualsYoYUTNet:
          prevYearUTNet.tam && currQuarterUTNet.tam
            ? ((currQuarterUTNet.tam - prevYearUTNet.tam) / prevYearUTNet.tam) * 100
            : null,

        /* COVERAGE */
        coverageWW: getSumOfNumbers(filteredCoverage.map((it) => it.CoverageWW ?? null)),
        coverageWWNet: getSumOfNumbers(filteredCoverage.map((it) => it.CoverageWWNet ?? null)),
        coverageWWTNet: getSumOfNumbers(filteredCoverage.map((it) => it.CoverageWWTNet ?? null)),
        coverageWWUTNet: getSumOfNumbers(filteredCoverage.map((it) => it.CoverageWWUTNet ?? null)),
        coverageMarket: getSumOfNumbers(filteredCoverage.map((it) => it.CoverageMarket ?? null)),
        coverageMarketNet: getSumOfNumbers(
          filteredCoverage.map((it) => it.CoverageMarketNet ?? null)
        ),
        coverageMarketTNet: getSumOfNumbers(
          filteredCoverage.map((it) => it.CoverageMarketTNet ?? null)
        ),
        coverageMarketUTNet: getSumOfNumbers(
          filteredCoverage.map((it) => it.CoverageMarketUTNet ?? null)
        ),

        /* CURRENT YEAR */
        currYearActualsRevenue: currQuarterNdp.actualsRevenue,
        currYearActualsRevenueNet: currQuarterNet.actualsRevenue,
        currYearActualsRevenueTNet: currQuarterTNet.actualsRevenue,
        currYearActualsRevenueUTNet: currQuarterUTNet.actualsRevenue,

        currYearEstimatedActualsRevenue: currQuarterNdp.estimatedActualsRevenue,
        currYearEstimatedActualsRevenueNet: currQuarterNet.estimatedActualsRevenue,
        currYearEstimatedActualsRevenueTNet: currQuarterTNet.estimatedActualsRevenue,
        currYearEstimatedActualsRevenueUTNet: currQuarterUTNet.estimatedActualsRevenue,

        currYearSobActual: currQuarterNdp.sob ?? currQuarterNdp.plainSob,
        currYearSobActualNet: currQuarterNet.sob ?? currQuarterNet.plainSob,
        currYearSobActualTNet: currQuarterTNet.sob ?? currQuarterTNet.plainSob,
        currYearSobActualUTNet: currQuarterUTNet.sob ?? currQuarterUTNet.plainSob,

        currYearPlansRevenue: currYearPlanNdp.actualsRevenue,
        currYearPlansRevenueNet: currYearPlanNet.actualsRevenue,
        currYearPlansRevenueTNet: currYearPlanTNet.actualsRevenue,
        currYearPlansRevenueUTNet: currYearPlanUTNet.actualsRevenue,

        currYearEstimatedPlansRevenue: currYearPlanNdp.estimatedActualsRevenue,
        currYearEstimatedPlansRevenueNet: currYearPlanNet.estimatedActualsRevenue,
        currYearEstimatedPlansRevenueTNet: currYearPlanTNet.estimatedActualsRevenue,
        currYearEstimatedPlansRevenueUTNet: currYearPlanUTNet.estimatedActualsRevenue,

        currYearSobPlan: currYearPlanNdp.sob ?? currYearPlanNdp.plainSob,
        currYearSobPlanNet: currYearPlanNet.sob ?? currYearPlanNet.plainSob,
        currYearSobPlanTNet: currYearPlanTNet.sob ?? currYearPlanTNet.plainSob,
        currYearSobPlanUTNet: currYearPlanUTNet.sob ?? currYearPlanUTNet.plainSob,

        currYearSalesPerformance: calculateSalesPerformance({
          actualsRevenue: currQuarterNdp.actualsRevenue,
          planRevenue: currYearPlanNdp.actualsRevenue,
        }),
        currYearSalesPerformanceNet: calculateSalesPerformance({
          actualsRevenue: currQuarterNet.actualsRevenue,
          planRevenue: currYearPlanNet.actualsRevenue,
        }),
        currYearSalesPerformanceTNet: calculateSalesPerformance({
          actualsRevenue: currQuarterTNet.actualsRevenue,
          planRevenue: currYearPlanTNet.actualsRevenue,
        }),
        currYearSalesPerformanceUTNet: calculateSalesPerformance({
          actualsRevenue: currQuarterUTNet.actualsRevenue,
          planRevenue: currYearPlanUTNet.actualsRevenue,
        }),

        currYearYoYGrowth: calculateYoYGrowth({
          currYearActualsRevenue: currQuarterNdp.actualsRevenue,
          prevYearActualsRevenue: prevYearNdp.actualsRevenue,
        }),

        currYearYoYGrowthNet: calculateYoYGrowth({
          currYearActualsRevenue: currQuarterNet.actualsRevenue,
          prevYearActualsRevenue: prevYearNet.actualsRevenue,
        }),

        currYearYoYGrowthTNet: calculateYoYGrowth({
          currYearActualsRevenue: currQuarterTNet.actualsRevenue,
          prevYearActualsRevenue: prevYearTNet.actualsRevenue,
        }),

        currYearYoYGrowthUTNet: calculateYoYGrowth({
          currYearActualsRevenue: currQuarterUTNet.actualsRevenue,
          prevYearActualsRevenue: prevYearUTNet.actualsRevenue,
        }),

        currYearQuarterTamPlan: currYearPlanNdp.tam,
        currYearQuarterTamPlanNet: currYearPlanNet.tam,
        currYearQuarterTamPlanTNet: currYearPlanTNet.tam,
        currYearQuarterTamPlanUTNet: currYearPlanUTNet.tam,

        currYearQuarterTamActuals: currQuarterNdp.tam,
        currYearQuarterTamActualsNet: currQuarterNet.tam,
        currYearQuarterTamActualsTNet: currQuarterTNet.tam,
        currYearQuarterTamActualsUTNet: currQuarterUTNet.tam,

        ...expectedExitFields,
      }
    })
    .filter((it) => !hasAppliedCuntriesFilter || it.partnersCount > 0)
}
